import { Controller } from "@hotwired/stimulus";
import { Modal } from 'bootstrap';

// The modal open when a toggle opens another one, which Bootstrap closes on the way. Bootstrap listens on
// the document while capturing, so this listens on the window, which captures first.
const openers = new WeakMap();

window.addEventListener('click', (event) => {
  const toggle = event.target.closest?.('[data-bs-toggle="modal"]');
  const selector = toggle?.dataset.bsTarget || toggle?.getAttribute('href');
  const target = selector && document.querySelector(selector);
  const open = document.querySelector('.modal.show');
  if (target && open && open !== target) openers.set(target, open);
}, true);

// Connects to data-controller="modal"
export default class extends Controller {
  static targets = ['modal', 'back'];
  static values = {
    show: String,
    closeOnVisit: Boolean,
    reloadOnShow: Boolean
  };

  connect() {
    const modal = this.modalTarget;

    // Opened from another modal: the header arrow goes back to it, as it was left.
    this.onShow = (event) => {
      if (event.target !== modal) return;

      if (modal.dataset.modalReturning) delete modal.dataset.modalReturning;
      else this.previous = openers.get(modal);
      openers.delete(modal);
      this.backTargets.forEach((button) => { button.hidden = !this.previous?.isConnected; });
      if (this.reloadOnShowValue) this.reloadFrames();
    };
    modal.addEventListener('show.bs.modal', this.onShow);

    if (this.closeOnVisitValue) {
      this.hideOnVisit = () => Modal.getInstance(modal)?.hide();
      document.addEventListener('turbo:before-visit', this.hideOnVisit);
    }

    if (!modal.classList.contains('moved')) {
      // Find or create the 'modals' div at the top of the body
      let modalsContainer = document.querySelector('.modals');
      if (!modalsContainer) {
        modalsContainer = document.createElement('div');
        modalsContainer.id = 'modals';
        modalsContainer.classList.add('modals');
        document.body.insertBefore(modalsContainer, document.body.firstChild);
      }

      this.removeStaleCopies(modalsContainer, modal);

      // Find the div you want to move (replace 'your-div-id' with the actual ID)
      modalsContainer.appendChild(modal);

      // Add a class to mark that the operation has been performed
      modal.classList.add('moved');

      if (this.showValue === 'true') {
        const bootstrapModal = new Modal(modal, {});
        bootstrapModal.toggle();
      }
    }
  }

  disconnect() {
    if (this.hideOnVisit) document.removeEventListener('turbo:before-visit', this.hideOnVisit);
    this.modalTarget.removeEventListener('show.bs.modal', this.onShow);
  }

  // Content changed elsewhere since the last opening: reload the frames already loaded, not those nested in them.
  // A loaded lazy frame is frozen without `src` (see common.ts): its address waits in `data-lazy-src`.
  reloadFrames() {
    this.modalTarget.querySelectorAll('turbo-frame[complete]').forEach((frame) => {
      if (frame.parentElement.closest('turbo-frame')) return;

      const src = frame.getAttribute('src') || frame.dataset.lazySrc;
      if (!src) return;

      frame.removeAttribute('complete');
      if (frame.getAttribute('src')) frame.reload();
      else frame.src = src;
    });
  }

  back() {
    const previous = this.previous;
    if (!previous?.isConnected) return;

    previous.dataset.modalReturning = 'true';
    this.modalTarget.addEventListener('hidden.bs.modal', () => Modal.getOrCreateInstance(previous).show(), { once: true });
    Modal.getInstance(this.modalTarget)?.hide();
  }

  initialize() {
    this.hideAllModalsOnUpdate();
  }

  // Une frame rechargée (onglet d'un index…) rend à nouveau sa modale, sous le même id. L'ancienne,
  // déjà sortie de la frame, resterait ici en premier : c'est elle qu'ouvrirait `data-bs-target="#id"`,
  // avec un contenu périmé. On la retire, ou à sa fermeture si elle est ouverte.
  removeStaleCopies(container, modal) {
    if (!modal.id) return;

    container.querySelectorAll(`#${CSS.escape(modal.id)}`).forEach((stale) => {
      if (stale === modal) return;

      const remove = () => {
        Modal.getInstance(stale)?.dispose();
        stale.remove();
      };
      if (stale.classList.contains('show')) {
        stale.addEventListener('hidden.bs.modal', remove, { once: true });
      } else {
        remove();
      }
    });
  }

  hideAllModalsOnUpdate() {
    document.addEventListener('turbo:before-stream-render', () => {
      const modals = document.querySelectorAll('.turbo-closed-modal');
      modals.forEach(modal => {
        const bootstrapModal = Modal.getInstance(modal);
        if (bootstrapModal) {
          bootstrapModal.hide();
        }
      });
    });
  }

}
